import React, { useMemo, useState } from 'react';
import { Search, Globe, MapPin, Building, ChevronDown, Check } from 'lucide-react';
import { type Scope, regions, communities } from '../../data/optionsData';
export function ScopeSelector({
  currentScope,
  onChange
}: {
  currentScope: Scope;
  onChange: (scope: Scope) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return {
      regions,
      communities
    };
    const query = searchQuery.toLowerCase();
    const filteredRegions = regions.filter(r => r.name.toLowerCase().includes(query));
    const filteredCommunities: typeof communities = {};
    Object.entries(communities).forEach(([regionId, comms]) => {
      const matchingComms = comms.filter(c => c.name.toLowerCase().includes(query));
      if (matchingComms.length > 0) {
        filteredCommunities[regionId] = matchingComms;
        if (!filteredRegions.find(r => r.id === regionId)) {
          const region = regions.find(r => r.id === regionId);
          if (region) filteredRegions.push(region);
        }
      }
    });
    return {
      regions: filteredRegions,
      communities: filteredCommunities
    };
  }, [searchQuery]);
  const handleSelect = (scope: Scope) => {
    onChange(scope);
    setIsOpen(false);
    setSearchQuery('');
  };
  return <div className="relative" data-id="element-469">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors w-full" data-id="element-470">
        {currentScope.level === 'global' && <Globe className="h-4 w-4 text-slate-500" data-id="element-471" />}
        {currentScope.level === 'region' && <MapPin className="h-4 w-4 text-blue-500" data-id="element-472" />}
        {currentScope.level === 'community' && <Building className="h-4 w-4 text-amber-500" data-id="element-473" />}
        <div className="flex-1 text-left" data-id="element-474">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-475">
            Viewing Catalog
          </p>
          <p className="text-sm font-medium text-slate-900 truncate" data-id="element-476">
            {currentScope.name}
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-477" />
      </button>

      {isOpen && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 max-h-[400px] flex flex-col" data-id="element-478">
          <div className="px-3 pb-2 border-b border-slate-100" data-id="element-479">
            <div className="relative" data-id="element-480">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-481" />
              <input type="text" placeholder="Search regions or communities..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-482" />
            </div>
          </div>
          <div className="overflow-y-auto flex-1" data-id="element-483">
            {(!searchQuery || 'all options'.includes(searchQuery.toLowerCase())) && <button onClick={() => handleSelect({
          level: 'global',
          name: 'All Options'
        })} className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-slate-50 ${currentScope.level === 'global' ? 'bg-slate-50' : ''}`} data-id="element-484">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-485">
                  <Globe className="h-4 w-4 text-slate-600" data-id="element-486" />
                </div>
                <div data-id="element-487">
                  <p className="text-sm font-medium text-slate-900" data-id="element-488">
                    All Options
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-489">
                    Base options for all regions
                  </p>
                </div>
                {currentScope.level === 'global' && <Check className="h-4 w-4 text-primary-600 ml-auto" data-id="element-490" />}
              </button>}
            {filteredData.regions.length > 0 && <>
                <div className="my-2 border-t border-slate-100" data-id="element-491" />
                <div className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-492">
                  Regions & Communities
                </div>
                {filteredData.regions.map(region => <div key={region.id} data-id="element-493">
                    <button onClick={() => handleSelect({
              level: 'region',
              regionId: region.id,
              name: region.name
            })} className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-slate-50 ${currentScope.level === 'region' && currentScope.regionId === region.id ? 'bg-slate-50' : ''}`} data-id="element-494">
                      <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-495">
                        <MapPin className="h-4 w-4 text-blue-600" data-id="element-496" />
                      </div>
                      <div className="flex-1" data-id="element-497">
                        <p className="text-sm font-medium text-slate-900" data-id="element-498">
                          {region.name}
                        </p>
                      </div>
                      {currentScope.level === 'region' && currentScope.regionId === region.id && <Check className="h-4 w-4 text-primary-600" data-id="element-499" />}
                    </button>
                    {(filteredData.communities[region.id] || communities[region.id])?.map(community => {
              if (searchQuery && !community.name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
              return <button key={community.id} onClick={() => handleSelect({
                level: 'community',
                regionId: region.id,
                communityId: community.id,
                name: community.name
              })} className={`w-full py-1.5 px-2 ml-12 mr-4 rounded text-left flex items-center gap-2 hover:bg-slate-50 ${currentScope.level === 'community' && currentScope.communityId === community.id ? 'bg-amber-50 text-amber-900' : 'text-slate-600'}`} data-id="element-500">
                          <Building className="h-3 w-3" data-id="element-501" />
                          <span className="text-sm" data-id="element-502">{community.name}</span>
                          {currentScope.level === 'community' && currentScope.communityId === community.id && <Check className="h-3 w-3 ml-auto" data-id="element-503" />}
                        </button>;
            })}
                  </div>)}
              </>}
            {searchQuery && filteredData.regions.length === 0 && <div className="px-4 py-6 text-center text-sm text-slate-500" data-id="element-504">
                No regions or communities found for "{searchQuery}"
              </div>}
          </div>
        </div>}
    </div>;
}